// src/components/modals/ViewSupplierModal.jsx
import { X } from 'lucide-react'
import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import SuppliersService from '../../services/suppliersService'
import { CustomerModel } from '../../utils/customerModel'

const ViewSupplierModal = ({ isOpen, onClose, supplier }) => {
  const [financialData, setFinancialData] = useState(null)
  const [loadingFinancialData, setLoadingFinancialData] = useState(false)

  // Load financial data when supplier changes
  useEffect(() => {
    if (supplier?._id) {
      loadFinancialData(supplier._id)
    }
  }, [supplier?._id])

  const loadFinancialData = async (supplierId) => {
    try {
      setLoadingFinancialData(true)
      console.log('🔄 Loading financial data for supplier:', supplierId)
      
      const result = await SuppliersService.getSupplierFinancialData(supplierId)
      
      if (result.success) {
        setFinancialData(result.data)
        console.log('✅ Financial data loaded:', result.data)
      } else {
        console.warn('⚠️ Failed to load financial data:', result)
        setFinancialData({
          totalPurchases: 0,
          totalPaid: 0,
          balanceDue: 0,
          paymentStatus: 'No Data',
          grnCount: 0,
          paymentDetails: []
        })
      }
    } catch (error) {
      console.error('❌ Error loading financial data:', error)
      setFinancialData({
        totalPurchases: 0,
        totalPaid: 0,
        balanceDue: 0,
        paymentStatus: 'Error',
        grnCount: 0,
        paymentDetails: []
      })
    } finally {
      setLoadingFinancialData(false)
    }
  }
  if (!isOpen || !supplier) return null

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-GB')
    } catch {
      return 'N/A'
    }
  }

  // Generate supplier ID display
  const getSupplierIdDisplay = (supplier) => {
    if (supplier._id) {
      return `SE${supplier._id.slice(-5).toUpperCase()}`
    }
    return 'N/A'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900">Supplier Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Basic Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-200">
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Supplier ID */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Supplier ID
                </label>
                <div className="text-lg font-semibold text-gray-900">
                  {getSupplierIdDisplay(supplier)}
                </div>
              </div>

              {/* Supplier Name */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Supplier name
                </label>
                <div className="text-lg font-semibold text-gray-900">
                  {supplier.name || 'N/A'}
                </div>
              </div>

              {/* Address */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Address
                </label>
                <div className="text-base text-gray-900">
                  {supplier.address || 'N/A'}
                </div>
              </div>

              {/* Mobile Number */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Mobile number
                </label>
                <div className="text-base font-medium text-gray-900">
                  {supplier.mobile ? CustomerModel.formatMobileNumber(supplier.mobile) : 'N/A'}
                </div>
              </div>

              {/* Contact Person */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Contact Person
                </label>
                <div className="text-base font-medium text-gray-900">
                  {supplier.contactPerson || 'N/A'}
                </div>
              </div>

              {/* Email */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Email
                </label>
                <div className="text-base text-gray-900">
                  {supplier.email || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-200">
              Financial Overview
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Total Purchases */}
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                <label className="block text-sm font-medium text-blue-600 mb-2">
                  Total Purchases (Rs.)
                </label>
                <div className="text-2xl font-bold text-blue-700">
                  {loadingFinancialData ? (
                    <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  ) : (
                    `Rs. ${(financialData?.totalPurchases || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  )}
                </div>
              </div>

              {/* Total Paid */}
              <div className="bg-green-50 rounded-lg p-6 border border-green-100">
                <label className="block text-sm font-medium text-green-600 mb-2">
                  Total Paid (Rs.)
                </label>
                <div className="text-2xl font-bold text-green-700">
                  {loadingFinancialData ? (
                    <div className="animate-spin h-6 w-6 border-2 border-green-500 border-t-transparent rounded-full"></div>
                  ) : (
                    `Rs. ${(financialData?.totalPaid || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  )}
                </div>
              </div>

              {/* Balance Due */}
              <div className="bg-orange-50 rounded-lg p-6 border border-orange-100">
                <label className="block text-sm font-medium text-orange-600 mb-2">
                  Balance Due (Rs.)
                </label>
                <div className="text-2xl font-bold text-orange-700">
                  {loadingFinancialData ? (
                    <div className="animate-spin h-6 w-6 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                  ) : (
                    `Rs. ${(financialData?.balanceDue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  )}
                </div>
              </div>
            </div>
            
            {/* Payment Status and Transaction Summary */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Payment Status */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Payment Status
                </label>
                <div className="flex items-center space-x-2">
                  {loadingFinancialData ? (
                    <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                  ) : (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      financialData?.paymentStatus === 'Fully Paid' 
                        ? 'bg-green-100 text-green-800' 
                        : financialData?.paymentStatus === 'Partially Paid'
                        ? 'bg-yellow-100 text-yellow-800'
                        : financialData?.paymentStatus === 'Unpaid'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {financialData?.paymentStatus || 'No Data'}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Transaction Count */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Total Transactions
                </label>
                <div className="text-lg font-semibold text-gray-900">
                  {loadingFinancialData ? (
                    <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                  ) : (
                    `${financialData?.grnCount || 0} Payment${(financialData?.grnCount || 0) !== 1 ? 's' : ''}`
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {supplier.notes && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Notes
              </h3>
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                <div className="text-base text-gray-700">
                  {supplier.notes || 'Cakes, Beverages'}
                </div>
              </div>
            </div>
          )}

          {/* Account Information */}
          {supplier.accountInformation && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                Account Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Account Name */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Account name
                  </label>
                  <div className="text-base font-medium text-gray-900">
                    {supplier.accountInformation.accountName || 'N/A'}
                  </div>
                </div>

                {/* Account Number */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Account number
                  </label>
                  <div className="text-base font-mono text-gray-900">
                    {supplier.accountInformation.accountNumber || 'N/A'}
                  </div>
                </div>

                {/* Bank Name */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Bank name
                  </label>
                  <div className="text-base font-medium text-gray-900">
                    {supplier.accountInformation.bankName || 'N/A'}
                  </div>
                </div>

                {/* Branch */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Branch
                  </label>
                  <div className="text-base text-gray-900">
                    {supplier.accountInformation.branchName || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status and Metadata */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-200">
              Status & Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Status
                </label>
                <div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    supplier.isActive !== false 
                      ? 'bg-orange-100 text-orange-800 border border-orange-200' 
                      : 'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}>
                    {supplier.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Created Date */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Created Date
                </label>
                <div className="text-base text-gray-900">
                  {formatDate(supplier.createdAt)}
                </div>
              </div>

              {/* Last Updated */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Last Updated
                </label>
                <div className="text-base text-gray-900">
                  {formatDate(supplier.updatedAt)}
                </div>
              </div>
            </div>

            {supplier.createdBy && (
              <div className="mt-6">
                <div className="bg-gray-50 rounded-lg p-4 inline-block">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Created By
                  </label>
                  <div className="text-base font-medium text-gray-900">
                    {supplier.createdBy.username || 'Unknown User'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

ViewSupplierModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  supplier: PropTypes.object
}

export default ViewSupplierModal